// OPS-01 — one-vendor own-fleet route planning from a fresh, paged candidate
// read. The board's legacy_unverified cards never grant an action here. The
// server locks and rechecks every exact stop before recording a route; this UI
// does not claim parcel custody, provider dispatch, or a physical handoff.
import { useCallback, useEffect, useRef, useState } from "react";
import { MapPinned, RotateCcw } from "lucide-react";

import { ApiError, apiGet, apiPost } from "../api";
import { useAuth } from "../auth";
import { newRequestId } from "../cases";
import type { FleetDriver, OperatorRoute, RouteCandidate, RouteCandidatePage } from "../types";

export default function Routes() {
  const { user } = useAuth();
  const canPlan = user?.role === "owner" || user?.role === "admin" || user?.role === "dispatcher";
  const [drivers, setDrivers] = useState<FleetDriver[]>([]);
  const [driverId, setDriverId] = useState("");
  const driverRef = useRef("");
  const requestSeq = useRef(0);
  const [candidates, setCandidates] = useState<RouteCandidate[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [name, setName] = useState("Route");
  const [requestId, setRequestId] = useState(() => newRequestId());
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<OperatorRoute | null>(null);
  const [readbackOk, setReadbackOk] = useState(false);

  useEffect(() => {
    apiGet<{ drivers: FleetDriver[] }>("/dispatch/fleet?include_offline=true")
      .then((value) => setDrivers(value.drivers))
      .catch((e) => setErr(e instanceof ApiError ? e.message : "Could not load the fleet"));
  }, []);

  const loadCandidates = useCallback(async (id: string, offset: number, append: boolean) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const page = await apiGet<RouteCandidatePage>(`/dispatch/route-candidates?driver_id=${encodeURIComponent(id)}&offset=${offset}`);
      if (driverRef.current !== id || requestSeq.current !== seq) return;
      setCandidates((current) => append ? [...current, ...page.items] : page.items);
      setNextOffset(page.next_offset);
      setErr(null);
    } catch (e) {
      if (driverRef.current === id && requestSeq.current === seq) {
        setErr(e instanceof ApiError ? e.message : "Could not load route candidates");
      }
    } finally {
      if (driverRef.current === id && requestSeq.current === seq) setLoading(false);
    }
  }, []);

  const chooseDriver = (id: string) => {
    driverRef.current = id;
    ++requestSeq.current;
    setDriverId(id);
    setCandidates([]);
    setSelected([]);
    setNextOffset(null);
    setResult(null);
    setReadbackOk(false);
    setRequestId(newRequestId());
    setErr(null);
    setLoading(false);
    if (id) void loadCandidates(id, 0, false);
  };

  const refresh = () => {
    setSelected([]);
    setRequestId(newRequestId());
    if (driverId) void loadCandidates(driverId, 0, false);
  };

  const selectedVendor = candidates.find((item) => selected.includes(item.id))?.vendor_id ?? null;
  const toggle = (item: RouteCandidate) => {
    if (selectedVendor && selectedVendor !== item.vendor_id && !selected.includes(item.id)) return;
    setSelected((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id]);
    setRequestId(newRequestId());
    setResult(null);
    setErr(null);
  };

  const create = async () => {
    if (!canPlan || !driverId || selected.length === 0 || busy) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    setReadbackOk(false);
    try {
      const response = await apiPost<{ route: OperatorRoute; replayed: boolean }>("/dispatch/routes", {
        client_request_id: requestId,
        driver_id: driverId,
        delivery_ids: selected,
        name: name.trim() || "Route",
      });
      setResult(response.route);
      let readbackError: string | null = null;
      try {
        const readback = await apiGet<{ route: OperatorRoute }>(`/dispatch/routes/${response.route.id}`);
        setResult(readback.route);
        setReadbackOk(true);
      } catch {
        readbackError = `Route ${response.route.id} was recorded, but readback failed. Do not submit a new request; inspect this route first.`;
      }
      setSelected([]);
      setRequestId(newRequestId());
      await loadCandidates(driverId, 0, false);
      if (readbackError) setErr(readbackError);
    } catch (e) {
      // Keep the SAME request id and selection: a lost ACK may already have
      // committed. Repeating this request can only read the exact route or
      // return a conflict; it cannot make a second route from a new id.
      setErr(`${e instanceof ApiError ? e.message : "Route outcome unknown"}. Retry only with this unchanged request, or refresh and reconcile.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="board-page">
      <div className="board-header"><h1>Own-fleet routes</h1></div>
      <p className="cap-note">
        Select pre-pickup stops already assigned to one rider and one vendor. These are current
        planning candidates, not the board’s unverified dispatch projection or proof of parcel custody.
        The server rechecks the exact selection at commit.
      </p>
      {!canPlan && <p className="cap-note">Your role can review candidates, but only owner, admin or dispatcher can plan a route.</p>}
      {err && <div className="banner-err" role="alert">{err}</div>}

      <section className="lane dlq-lane">
        <header className="lane-head"><span><MapPinned size={13} /> Plan one route</span></header>
        <div className="lane-body">
          <label className="cap-note" htmlFor="route-driver">Rider</label>
          <select id="route-driver" className="recover-input" value={driverId} onChange={(e) => chooseDriver(e.target.value)} disabled={busy}>
            <option value="">Choose a rider</option>
            {drivers.map((driver) => <option key={driver.id} value={driver.id}>{driver.name} · {driver.pool} pool · {driver.status}</option>)}
          </select>
          {driverId && (
            <>
              <div className="recover-actions">
                <span className="cap-note">{candidates.length} candidate{candidates.length === 1 ? "" : "s"} loaded</span>
                <button className="courier-btn" disabled={loading || busy} onClick={refresh}><RotateCcw size={12} /> Refresh candidates</button>
              </div>
              {candidates.length === 0 && !loading && <p className="lane-empty">No eligible unrouted stops for this rider.</p>}
              {candidates.map((item) => (
                <label className="dlq-row" key={item.id}>
                  <input
                    type="checkbox"
                    checked={selected.includes(item.id)}
                    disabled={!canPlan || busy || (selected.length >= 50 && !selected.includes(item.id)) || Boolean(selectedVendor && selectedVendor !== item.vendor_id)}
                    onChange={() => toggle(item)}
                    aria-label={`Select ${item.order_name || item.id}`}
                  />
                  <span className="dlq-main">
                    <span className="dlq-event">{item.order_name || item.id.slice(0, 8)} · {item.vendor_name}</span>
                    <span className="dlq-sub">Recorded {item.status} · {item.city || "city unknown"}{item.has_coordinates ? " · location recorded" : " · no coordinates"}</span>
                  </span>
                </label>
              ))}
              {nextOffset !== null && (
                <button className="courier-btn" disabled={loading || busy} onClick={() => void loadCandidates(driverId, nextOffset, true)}>
                  {loading ? "Loading…" : "Load more candidates"}
                </button>
              )}
              <label className="cap-note" htmlFor="route-name">Route name</label>
              <input id="route-name" className="recover-input recover-wide" maxLength={120} value={name} disabled={busy || !canPlan}
                onChange={(e) => { setName(e.target.value); setRequestId(newRequestId()); }} />
              <div className="recover-actions">
                <button className="courier-btn" disabled={!canPlan || busy || selected.length === 0 || selected.length > 50} onClick={() => void create()}>
                  {busy ? "Recording…" : `Record route for ${selected.length} stop${selected.length === 1 ? "" : "s"}`}
                </button>
                <span className="cap-note">No pickup, delivery, cash or custody state changes.</span>
              </div>
            </>
          )}
        </div>
      </section>

      {result && (
        <section className="lane dlq-lane">
          <header className="lane-head"><span>{readbackOk ? "Recorded route · read back" : "Recorded route · readback pending"}</span></header>
          <div className="lane-body">
            <p className="cap-note">{result.name} · {result.id} · {result.status} · {result.stop_count} stops</p>
            <p className="cap-note">{result.authority_note}</p>
            <ol>{result.stops.map((stop) => <li key={stop.id}>#{stop.sequence} {stop.order_name || stop.id} · recorded {stop.status}</li>)}</ol>
          </div>
        </section>
      )}
    </div>
  );
}
